import { useLayoutEffect, useState, type RefObject } from 'react'

/**
 * Hvad mærkaterne printes på (beslutning 106).
 *
 * - `roll62`: Brother QL-1110NWB med 62 mm endeløs rulle (DK-22205). Hvert mærkat er sin egen
 *   side på 62 x 40 mm, så printeren klipper efter hvert.
 * - `sheet`: almindeligt ark med selvklæbende mærkater, som før.
 *
 * Valget huskes i browseren - det er den enkelte pc, der står ved printeren.
 */
export type LabelMedia = 'roll62' | 'sheet'

const storageKey = 'tcpss.labelMedia'

/** Længden på ét mærkat på rullen. Bredden er rullens 62 mm. */
export const rollLabelHeightMm = 40

export function useLabelMedia(): [LabelMedia, (media: LabelMedia) => void] {
  const [media, setMedia] = useState<LabelMedia>(() => {
    try {
      return localStorage.getItem(storageKey) === 'sheet' ? 'sheet' : 'roll62'
    } catch {
      return 'roll62'
    }
  })

  function choose(next: LabelMedia) {
    setMedia(next)
    try {
      localStorage.setItem(storageKey, next)
    } catch {
      // Privat vindue - valget gælder bare, til siden lukkes.
    }
  }

  return [media, choose]
}

/** Valget mellem rullen og arket, til værktøjslinjen over mærkaterne. */
export function LabelMediaPicker({ media, onChange }: { media: LabelMedia; onChange: (media: LabelMedia) => void }) {
  return (
    <label className="row small muted" style={{ gap: 6 }}>
      Printer
      <select value={media} onChange={(event) => onChange(event.target.value as LabelMedia)}>
        <option value="roll62">Brother QL, 62 mm endeløs</option>
        <option value="sheet">Ark med mærkater</option>
      </select>
    </label>
  )
}

/**
 * Sætter papirets størrelse, mens siden er åben. Står i en `<style>`, der kun findes på
 * mærkatsiden - en `@page` i styles.css ville også ramme alt andet, der printes.
 */
export function LabelPageStyle({ media }: { media: LabelMedia }) {
  if (media !== 'roll62') return null
  return <style>{`@page { size: 62mm ${rollLabelHeightMm}mm; margin: 0; }`}</style>
}

/** Sådan sættes printeren op - står under knapperne, når rullen er valgt. */
export function RollPrintHint({ media }: { media: LabelMedia }) {
  if (media !== 'roll62') return null
  return (
    <p className="tiny muted no-print" style={{ margin: '0 0 12px' }}>
      I printvinduet: vælg Brother QL-1110NWB, papirstørrelse <strong>62 mm x {rollLabelHeightMm} mm</strong> (eller
      "62mm" endeløs), margener <strong>Ingen</strong> og skalering <strong>100 %</strong>. Findes størrelsen ikke,
      oprettes den én gang i Brother-driverens indstillinger under "Papirstørrelse".
    </p>
  )
}

/**
 * Skalerer indholdet ned, så det altid kan være inden for mærkatets faste mål - et langt sitenavn
 * eller en ekstra linje må ikke blive skåret af nederst (Simon, 2026-09-23). Indholdet gøres aldrig
 * større, kun mindre. Regnes igen, når skrifterne er hentet, og lige før der printes.
 */
export function useFitToLabel(ref: RefObject<HTMLElement>, deps: unknown[]) {
  useLayoutEffect(() => {
    const content = ref.current
    const box = content?.parentElement
    if (!content || !box) return

    const fit = () => {
      content.style.zoom = ''
      const style = getComputedStyle(box)
      const height = box.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom)
      const width = box.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight)
      const factor = Math.min(1, height / content.scrollHeight, width / content.scrollWidth)
      // Et lille hak under for en sikkerheds skyld - afrunding i printeren skal ikke koste en linje.
      if (factor < 1) content.style.zoom = String(Math.floor(factor * 100) / 100 - 0.01)
    }

    fit()
    void document.fonts?.ready.then(fit)
    window.addEventListener('beforeprint', fit)
    return () => window.removeEventListener('beforeprint', fit)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/** Klassen på arket med mærkater. */
export function labelSheetClass(media: LabelMedia) {
  return media === 'roll62' ? 'label-sheet label-sheet--roll' : 'label-sheet'
}
